import type { RecordingId, RecordingRenewed, SessionId, VideoRenewed } from "@don/wire";

import {
  clockLabel,
  dateTimeLabel,
  expiryMarker,
  longDateLabel,
  sessionTitle,
  videoUntil,
  type ExpiryMarker,
  type LibraryRow,
} from "./row.js";

/**
 * Renewal and owner delete: where they come from, and what the screen says when they do not
 * happen (issue 30, stories 24 and 38).
 *
 * Pure and outside any component, for the reason `row.ts` and `people.ts` are: the rules
 * worth asserting are what a state *means*, and a rule that lives inside a render function
 * can only be asserted through a browser.
 */

/**
 * Where the two owner actions go.
 *
 * **A port, not a `fetch`.** `POST /sessions/{id}/renew` and `POST /sessions/{id}/delete`
 * exist and the Console does not call them: its one data source reads the library and one
 * session and nothing else (`backend/adapter.ts`, issue 90). This is the boundary a real
 * adapter fills, and it is where a `fetch` goes on the day the adapter calls these routes.
 *
 * **`renew` answers what the server decided**, rather than the screen adding 30 days to its
 * own clock. There is no stored expiry anywhere (issue 19's Decision 1): the date is
 * computed from the session's own consent record every time, so a Console that moved its own
 * chip would print a deletion date nothing in the database holds. This is issue 31's
 * Decision 4 on a second pair of actions.
 *
 * **`destroy` takes a session and nothing else.** Deletion scope is derived from the
 * authority the server mints, never passed beside it (ADR 0007), so there is no argument
 * here through which a caller could widen what a deletion takes.
 *
 * Both reject rather than resolving quietly when the action did not happen. A refusal and a
 * failure are two different states the screen renders, and neither is an outcome; a `renew`
 * that was refused rejects with a value {@link isRenewalRefusal} recognises.
 */
export interface RetentionSource {
  renew(sessionId: SessionId): Promise<VideoRenewed>;
  destroy(sessionId: SessionId): Promise<void>;
  /**
   * A recording's two (issue 322): `POST /recordings/{id}/renew` and `/delete`, which act on
   * every segment. A recording row sends its recording id here and never to a session call (321
   * code-quality P1-1); `App`'s `retentionTarget` decides which.
   */
  renewRecording(recordingId: RecordingId): Promise<RecordingRenewed>;
  destroyRecording(recordingId: RecordingId): Promise<void>;
}

/**
 * What the screen is doing about **one session's** renewal, so it can say so.
 *
 * Four states rather than a boolean, which is the shape issue 26 had to add back in rework
 * and issue 31 reused: *not asked*, *in flight*, *refused* and *the request failed* are four
 * different situations, and collapsing them makes the screen render a finished verdict
 * during every round trip and every failure.
 *
 * `refused` and `failed` are separate because they are different facts about the recording.
 * A refusal is the server saying the video term has already ended, which is final; a failure
 * is the Console not knowing, which is not.
 *
 * **The session is the key and not a field.** A state carrying its own session id was a
 * field nothing read, so one refusal became a sentence about whichever recording was open
 * next and `Renew all N`'s concurrent settles overwrote each other until only the last one
 * survived. Both are the same defect: a per-session fact held once.
 */
export type RenewalState =
  | { readonly kind: "idle" }
  | { readonly kind: "working" }
  | { readonly kind: "refused" }
  | { readonly kind: "failed" };

/**
 * What the screen is doing about each session's renewal.
 *
 * A batch carries one entry per session it acted on, so a refusal among successes is a
 * statement the screen still has when the last of them settles.
 */
export type RenewalStates = Readonly<Record<SessionId, RenewalState>>;

/** Nothing asked about anything. */
export const NO_RENEWALS: RenewalStates = {};

const IDLE: RenewalState = { kind: "idle" };

/** What this screen is doing about this session, which is nothing unless it was asked. */
export function renewalFor(
  states: RenewalStates,
  sessionId: SessionId,
): RenewalState {
  return states[sessionId] ?? IDLE;
}

/**
 * One session's renewal state, moved.
 *
 * Merged rather than replaced, which is what lets `Renew all N` hold four outcomes at once.
 * `idle` drops the key instead of storing it: a renewal that worked has nothing to say, and
 * a map that grew an entry per successful renewal would keep a batch's whole history.
 */
export function withRenewalState(
  states: RenewalStates,
  sessionId: SessionId,
  state: RenewalState,
): RenewalStates {
  const next = { ...states };
  if (state.kind === "idle") delete next[sessionId];
  else next[sessionId] = state;
  return next;
}

/** A session a renewal did not happen for, and which of the two ways it did not. */
export interface RenewalOutcome {
  readonly row: LibraryRow;
  readonly kind: "refused" | "failed";
}

/**
 * Every renewal this library has something to report about, in the library's own order.
 *
 * Ordered by the rows rather than by which call settled first, so what the owner reads does
 * not depend on network timing. A session the library no longer holds reports nothing: a
 * deleted row has no chip for a notice to sit under.
 */
export function renewalOutcomes(
  states: RenewalStates,
  rows: readonly LibraryRow[],
): RenewalOutcome[] {
  const outcomes: RenewalOutcome[] = [];
  for (const row of rows) {
    const state = renewalFor(states, row.sessionId);
    if (state.kind === "refused" || state.kind === "failed") {
      outcomes.push({ row, kind: state.kind });
    }
  }
  return outcomes;
}

/** The two ways a renewal did not happen, each with the rows it happened to. */
export interface RenewalNotices {
  /** One per refusal, in the library's order. Each is named. */
  readonly refusals: readonly LibraryRow[];
  /** Every failure, which the screen says in one line. */
  readonly failed: readonly LibraryRow[];
}

/**
 * {@link renewalOutcomes}, split the way the screen renders them.
 *
 * Here rather than in the component, for the reason this whole module is here: which
 * outcomes are named one by one and which are folded is a rule, and a rule inside a render
 * function can only be asserted through a browser.
 */
export function renewalNotices(
  states: RenewalStates,
  rows: readonly LibraryRow[],
): RenewalNotices {
  const outcomes = renewalOutcomes(states, rows);
  return {
    refusals: outcomes.filter((it) => it.kind === "refused").map((it) => it.row),
    failed: outcomes.filter((it) => it.kind === "failed").map((it) => it.row),
  };
}

/**
 * A rejection the adapter marks as the server's refusal rather than as a failed request.
 *
 * **Structural, not a class.** The two cases are different facts about the recording: a
 * refusal is the server saying the video term has already ended, which is final, and a
 * failure is the Console not knowing, which is not. A screen that could not tell them apart
 * would have to pick one message and be wrong half the time. It is a marked property rather
 * than an `instanceof` so that an adapter written outside this module's realm, which is what
 * every test driving the page through `window.__console` is, can produce one.
 */
export function isRenewalRefusal(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { refused?: unknown }).refused === true
  );
}


/**
 * The session page's notice for a renewal the server refused (screen catalog
 * `session-renew-refused`): **Not renewed.** The video is still kept until Oct 31. It names the
 * day this page's own date says (`videoUntil`), which the refusal did not move; the notice
 * claims no renewal and no deletion. The session's renewal failing to arrive at all is the
 * library's one-video sentence ({@link failedNotice}), so the two screens say it one way.
 */
export function refusedOnSession(row: LibraryRow, timeZone?: string): { heading: string; body: string } {
  return {
    heading: "Not renewed.",
    body: `The video is still kept until ${videoUntil(row.videoExpiresAtMs, timeZone).label}.`,
  };
}

/**
 * A row's marker, given what the screen knows about its renewal (issue 195).
 *
 * A **refused** renewal is the server saying the video term has already ended, which is final
 * and outranks this page's clock: the row reads `Video deleted` (screen catalog
 * `sessions-renewal-refused`, s10) and offers nothing. A failure is the Console not knowing,
 * so the marker stays what the clock says.
 */
export function markerFor(
  nowMs: number,
  row: LibraryRow,
  renewal: RenewalState,
  timeZone?: string,
): ExpiryMarker {
  if (renewal.kind === "refused") {
    return { level: "gone", text: "Video deleted", short: "Video deleted" };
  }
  return expiryMarker(nowMs, row, timeZone);
}

/**
 * What `Renew all (n)` counts and renews: every row whose marker is `soon` or `urgent`
 * (PRD module 6, the catalog's three). The set, not a count, so the label and the requests are
 * one answer. A refused row is not in it: it reads `Video deleted` and a second ask would be
 * refused identically.
 */
export function atRisk(
  nowMs: number,
  rows: readonly LibraryRow[],
  states: RenewalStates,
): LibraryRow[] {
  return rows.filter((row) => {
    const level = markerFor(nowMs, row, renewalFor(states, row.sessionId)).level;
    return level === "soon" || level === "urgent";
  });
}

/** Whether every renewal a batch asked for has answered. */
export function batchSettled(batch: readonly SessionId[], states: RenewalStates): boolean {
  return batch.every((id) => renewalFor(states, id).kind !== "working");
}

/**
 * How many of a settled batch renewed. A session the batch asked about that is back to `idle`
 * is one whose renewal worked: `withRenewalState` drops the key on success, and every ask
 * marks it `working` first (`App`'s `renewSession`).
 */
export function renewedIn(batch: readonly SessionId[], states: RenewalStates): number {
  return batch.filter((id) => renewalFor(states, id).kind === "idle").length;
}

const videos = (n: number) => `${n} ${n === 1 ? "video" : "videos"}`;

/** One refused session in the refused notice: its name (set bold) and the rest of its sentence. */
export interface RefusedItem {
  readonly row: LibraryRow;
  readonly name: string;
  readonly rest: string;
}

export interface RefusedNotice {
  /** `Renewed 2 videos.`, or `null` when nothing in the batch renewed. */
  readonly lead: string | null;
  readonly items: readonly RefusedItem[];
}

/** The refused sentence's fixed middle, exported as the bundle's positive control. */
export const REFUSED_BECAUSE =
  " wasn't renewed because its video had reached its deletion date; its transcript, summary " +
  "and knowledge stay until ";

/**
 * The refused notice (screen catalog `sessions-renewal-refused`): `Renewed 2 videos. **Punch
 * list export for the site super** wasn't renewed because its video had reached its deletion
 * date; its transcript, summary and knowledge stay until Sep 2, 2027.` One sentence per
 * refusal, each naming its session by `sessionTitle`.
 *
 * `sessionTitle` is not unique (under `Derivation::Off` it is the day and person), and the
 * notice sits above the list rather than beside its rows, so a name two refusals share gains
 * what tells them apart: an untitled session its clock time, a titled one its person and its
 * day and time. A name nobody else in the notice shares stays the catalog's bare title.
 */
export function refusedNotice(
  refused: readonly LibraryRow[],
  renewed: number,
  nowMs: number,
  timeZone?: string,
): RefusedNotice {
  const names = refused.map((row) => sessionTitle(row, timeZone));
  const items = refused.map((row, index) => {
    const base = names[index]!;
    const shared = names.filter((each) => each === base).length > 1;
    const which =
      row.title === null || row.title === ""
        ? clockLabel(row.startedAtMs, timeZone)
        : `${row.person}, ${dateTimeLabel(row.startedAtMs, timeZone)}`;
    return {
      row,
      name: shared ? `${base} · ${which}` : base,
      rest: `${REFUSED_BECAUSE}${longDateLabel(nowMs, row.derivedExpiresAtMs, timeZone)}.`,
    };
  });
  return { lead: renewed > 0 ? `Renewed ${videos(renewed)}.` : null, items };
}

/**
 * The failed notice (screen catalog `sessions-renewal-failed`): **No videos were renewed.**
 * The server couldn't be reached, so their deletion dates haven't changed. One sentence for the
 * whole set, because one source that is down fails every call in a batch. The catalog names the
 * all-failed batch only; one video and a partial batch take the same two sentences in their
 * own number.
 */
export function failedNotice(
  failed: readonly LibraryRow[],
  renewed: number,
): { heading: string; body: string } {
  const one = failed.length === 1;
  const heading =
    renewed > 0
      ? `${failed.length} of ${videos(failed.length + renewed)} ${one ? "wasn't" : "weren't"} renewed.`
      : one
        ? "The video wasn't renewed."
        : "No videos were renewed.";
  const body = one
    ? "The server couldn't be reached, so its deletion date hasn't changed."
    : "The server couldn't be reached, so their deletion dates haven't changed.";
  return { heading, body };
}

/** The owner delete's confirmation, which decisions §(d)'s "no confirmation" is not about. */
export const DELETE_CONFIRM_TITLE = "Delete this session?";

/**
 * What the owner is agreeing to (screen catalog `session-delete`).
 *
 * **It names the writing as well as the video**, because `OwnerDelete`'s scope is everything
 * (ADR 0007). Every other path in this product keeps the transcript, the summary and the
 * knowledge for a year after the video goes, so a confirmation that named only the video
 * would be asking for consent to a smaller act than the one about to happen. It claims
 * nothing about what is kept: the audit row and the PHI-free counters survive the destroy
 * (ADR 0016), so the sentence names what goes and stops.
 */
export const DELETE_CONFIRM_BODY =
  "This deletes the video, transcript, summary and knowledge. It can't be undone.";

/**
 * A delete that did not happen.
 *
 * Decisions §(b): the UI never claims a deletion it has not performed. So a failed delete
 * leaves the row in the library and says so, rather than removing it optimistically and
 * telling the owner a session is gone that is still there.
 */
export const DELETE_FAILED = {
  heading: "The session wasn't deleted.",
  body: "It's still in your library.",
} as const;

/**
 * A delete that did happen.
 *
 * The counterpart to {@link DELETE_FAILED}, and it exists for the reason that one does.
 * Decisions §(b) forbids the UI claiming a deletion it has not performed; it does not
 * license saying nothing about one it has. What the owner otherwise gets is a route change
 * and a list one row shorter, which on a library of near-identical rows is invisible and on
 * a scrolled one may be off screen.
 *
 * The library's notice reads `Deleted **<title>**.` (screen catalog `sessions-deleted-notice`),
 * so what the screen holds is the name, by `sessionTitle` as the row named it. What the delete
 * took was said before the fact, by {@link DELETE_CONFIRM_BODY}; the notice does not restate it
 * (P21).
 */
export function deletedTitle(row: LibraryRow, timeZone?: string): string {
  return sessionTitle(row, timeZone);
}

/** The confirmation's two controls: the way out is the primary, the delete the outline. */
export const DELETE_KEEP_LABEL = "Keep it";
export const DELETE_CONFIRM_LABEL = "Delete";

/**
 * The library with one row's video date moved to the one the server decided.
 *
 * **`derivedExpiresAtMs` is untouched**, which is what the red marker's detail sentence says:
 * renewing keeps the video another term, and the transcript, Summary and Observations stay
 * until their own date, which renewal does not change.
 *
 * A session the library does not hold leaves it unchanged rather than being appended. The
 * answer says a video term moved; it does not say a recording exists that the owner's list
 * has not been told about.
 */
export function withRenewal(
  rows: readonly LibraryRow[],
  renewed: VideoRenewed,
): LibraryRow[] {
  return withVideoExpiry(rows, renewed.session_id, renewed.video_expires_at_ms);
}

/**
 * The library with one row's video expiry moved to the date the server answered, by the row's
 * key (issue 322): a session's id, or a recording's, whose segments move with it, since a
 * recording's renewal moves every segment (`POST /recordings/{id}/renew`).
 */
export function withVideoExpiry(
  rows: readonly LibraryRow[],
  key: SessionId,
  videoExpiresAtMs: number,
): LibraryRow[] {
  return rows.map((row) =>
    row.sessionId === key
      ? {
          ...row,
          videoExpiresAtMs,
          ...(row.segments === undefined
            ? {}
            : { segments: row.segments.map((segment) => ({ ...segment, videoExpiresAtMs })) }),
        }
      : row,
  );
}

/** The library with one session gone: decisions §(a), a deleted session does not render. */
export function withoutSession(
  rows: readonly LibraryRow[],
  sessionId: SessionId,
): LibraryRow[] {
  return rows.filter((row) => row.sessionId !== sessionId);
}
